import store from '@/store/store.js'

const NO_APPLY = "NO APLICA";
const TEXT_SGA_NOT_DANGER = "No es una sustancia o mezcla peligrosa según los criterios de clasificación de SGA";

/* 
 * Valida si los datos del documento PDF son validos
 * @param {Object} data - Datos del producto
 * @returns {boolean}
 */
export function isValidDocumentPdf(data) {  

    let product = data.response;

    if (!product || typeof product !== 'object') return false;
    if (!product.Nombre || typeof product.Nombre !== 'string' || product.Nombre.trim() === 'NO REGISTRA' && 
    !product['Número CAS'] || typeof product['Número CAS'] !== 'string' || product['Número CAS'].trim() === 'NO REGISTRA' &&
    !product.Fabricante || typeof product.Fabricante !== 'string') return false;
    
    return true;
}

/* 
 * Datos básicos del producto
 * @param {Object} data - Datos del producto
 * @returns {Promise<void>}
 */
export async function setProductData({ response, action }) {    
    // Datos generales del producto
    store.commit('productos/updateSelectedProduct', response);

    // Datos del fabricante
    await setManufacturerData(response);

    // Componentes del producto
    setComponentsData(response, action);
}

/* 
 * Setea los datos del fabricante en el store
 * @param {Object} data - Datos del producto
 * @returns {Promise<void>}
 */
export async function setManufacturerData(data) {
    if (!(data && 'Fabricante' in data && typeof data.Fabricante === 'string')) return;

    const nameProducer = data.Fabricante.substring(0, 6);
    const listProducers = await store.dispatch('productos/searchFabricantesByName', nameProducer)

    if (listProducers && listProducers.length > 0) {
        store.commit('productos/setIdFabricante', listProducers[0].id)
    }
}

/* 
 * Setea los datos del SGA en el store
 * @param {Object} data - Datos del producto
 * @returns {Array}
 */
export function getParseDataSGA(data) {
    // Si alguno de los dos campos está presente, usarlo, y validar que sea un array, si no, retornar array vacío
    let sgaArray = [];

    if (data && Array.isArray(data['SGA'])) {
        sgaArray = data['SGA'];
    } else if (data && Array.isArray(data['SGA del producto'])) {
        sgaArray = data['SGA del producto'];
    }

    // Formatear posibles respuestas de SGA        
    // 1. [{"id": "No es una sustancia o mezcla peligrosa según los criterios de clasificación de SGA"}]
    // 2. [{"id":"74"},{"id":"77"}]
    sgaArray = sgaArray.map(item => `${ item.id === TEXT_SGA_NOT_DANGER ? item.id : +item.id}`);

    // Si no hay SGA, se debe enviar como producto no peligroso
    if (sgaArray.length === 0) {
        sgaArray = [TEXT_SGA_NOT_DANGER];
    }

    return sgaArray;
}

/* 
 * Datos del UN en el store
 * @param {Object} data - Datos del producto
 * @returns {Object}
 */
export function getParseDataUN(data) {
    // Asegura que el objeto retornado tenga siempre las propiedades 'class' (array de string) y 'un' (string)
    let result = { un: "NO REGULADO", class: [] };

    if (data && typeof data['UN'] === 'object' && data['UN'] !== null) {
        const unObj = data['UN'];
        result.un = typeof unObj.un === 'string' ? unObj.un : result.un;
        if (Array.isArray(unObj.class)) {
            // Filtra para asegurar que todos los elementos sean string
            result.class = unObj.class.filter(c => typeof c === 'string' || typeof c === 'number').map(c => String(c));
        } else if (typeof unObj.class === 'string' || typeof unObj.class === 'number') {
            result.class = [String(unObj.class)];
        } else {
            result.class = [];
        }
    }

    return result;
}

// Esta función asegura que siempre se devuelvan todas las claves requeridas en el objeto epps,
// con arrays de enteros/strings o, si no hay datos, con un array que contiene "NO APLICA".
export function getParseDataEPPS(data) {
    const keys = [
        "id_ocular",
        "id_cutaneo",
        "id_respiratorio",
        "id_calzado",
        "id_traje"
    ];

    let epps = typeof data['epps'] === 'object' && data['epps'] !== null ? data['epps'] : {};
    const result = {};

    keys.forEach(key => {
        let values = epps[key];

        // Caso: string|number directo válido
        if ((typeof values === 'string' || typeof values === 'number') && values !== '' && values !== null && values !== undefined) {
            if (/^[0-9]+$/.test(values.toString())) {
                result[key] = [values.toString()];
            } else if (values.toString().toUpperCase().includes(NO_APPLY)) {
                result[key] = [NO_APPLY];
            } else {
                result[key] = [NO_APPLY];
            }
        }
        // Caso: array
        else if (Array.isArray(values)) {
            // Rescatar solo números
            const numericValues = values.filter(v => /^[0-9]+$/.test(v));

            if (numericValues.length > 0) {
                result[key] = numericValues;
            } else if (values.length === 1 && values[0].toString().toUpperCase().includes(NO_APPLY)) {
                result[key] = [NO_APPLY];
            } else {
                result[key] = [NO_APPLY];
            }
        }
        // Caso: vacío o inválido
        else {
            result[key] = [NO_APPLY];
        }
    });

    return result;
}

/* 
 * Setea los datos de los componentes en el store
 * @param {Object} data - Datos del producto
 * @returns {void}
 */
export function setComponentsData(data, action) {
    let baseDataComponents = (!(data['Componentes del producto'] || typeof data['Componentes del producto'] === 'object')) ? [] : data['Componentes del producto']
    store.commit('productos/setComponentsReadFds', {
        components: baseDataComponents,
        action
    })

    const components = store.state.productos.componentsReadFds.map(component => {
        return { ...component,
            visible: true,
            sub_product: false
        }
    })
    
    store.commit('nuevocas/setComponentesProducto', components)
}

export function getPayloadCreateProductWithIa({ 
    action,
    product, 
    filter, 
    customFields,
    dataReadPdf = null
} = {}) {

    const formData = new FormData();   

    // Almacenar CACHE de los datos leídos del PDF
    formData.append('cache', JSON.stringify(dataReadPdf));

    // Archivo del documento FDS
    formData.append('file', product.file);
    formData.append('date_document', product.dateDocument);
    formData.append('language_code', product.languageCode || '');
    formData.append('type_document', 'FDS');

    // Datos generales del producto
    let generalDataProductos = {
        name: product['nombre'],
        synonyms: product['sinonimos'],
        cas: product['cas'],
        state: product['estado'],
        manufacturer_id: product['id_fabricante'],
        use: product['uso'],
        observation: product['observacion'],
        disposition_and_treatment: product['disposiciontratamiento']
    };

    // ID del producto: Solo se envía si es una actualización
    if (action === 'UPDATE') {
        generalDataProductos.id = product['id'];
    }

    formData.append('product', JSON.stringify(generalDataProductos));

    // Componentes del producto
    formData.append('components', JSON.stringify(product.components));

    // SGA
    formData.append('sga', JSON.stringify(product.sga));

    // UN
    formData.append('un', JSON.stringify(product.un));

    // EPPS
    formData.append('epps', JSON.stringify(product.epps));

    // Datos de la empresa
    formData.append('company', JSON.stringify({
        id: filter.company.id,
        locations: filter.location_id,
        custom_fields: customFields.filter(field => field.id && field.valor).map(field => ({
            id: field.id,
            value: field.valor
        }))
    }));

    return formData;
}